'use client'

import React, { useState, useEffect } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { useLazyQuery } from '@apollo/client'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { columns } from './components/columns'
import { DataTableToolbar } from '@/components/shared/data-table-manual/data-table-toolbar-manual'
import { Button } from '@/components/ui/button'
import { DELETE_MANY_CONTENT_BLOCKS, GET_CONTENT_BLOCKS } from './contentBlockQueries'
import ContentBlockForm from './components/ContentBlockForm'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation'
import { PageContent } from '@/components/shared/page-content'
import { getOrderSortParams } from '@/utils/orderUtils'

export default function ContentBlocks() {
  const [searchTerm, setSearchTerm] = useState('')
  const [tableData, setTableData] = useState([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  )

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  })

  const [loadContentBlocks, { data, loading }] = useLazyQuery(
    GET_CONTENT_BLOCKS,
    {
      variables: {},
      fetchPolicy: 'cache-and-network',
    }
  )

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_CONTENT_BLOCKS,
    refetchQueries: ['GET_CONTENT_BLOCKS'],
    messages: {
      success: 'Content blocks deleted successfully',
      error: 'Failed to delete content blocks',
    },
    itemName: 'content block',
  })

  // Load content blocks data
  useEffect(() => {
    loadContentBlocks({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        orderBy: getOrderSortParams(order),
      },
    })
  }, [searchTerm, pagination, order, loadContentBlocks])

  // Reset to first page when search or page size changes
  useEffect(() => {
    setPagination(p => ({ ...p, pageIndex: 0 }))
  }, [searchTerm, order])

  // Update table data when query results change
  useEffect(() => {
    if (data) {
      setTableData(data.findManyContentBlock)
      setTableDataCount(data.findManyContentBlockCount ?? 0)
    }
  }, [data])

  const openContentBlockForm = useOpenDrawer(ContentBlockForm)

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection title="Content Blocks" />
        <Button onClick={openContentBlockForm}>Create New +</Button>
      </div>

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        onRowClick={openContentBlockForm}
        isLoading={loading}
        toolbar={(table) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: 'Filter content blocks...',
                accessor: 'name',
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [],
            }}
          />
        )}
      />
    </PageContent>
  )
}
